// Conditional content: whether a given recipient's copy keeps a block or drops it.
//
// One rule, one test: show the content only if the test passes. There is deliberately no
// "otherwise" branch and no per-rule choice for a missing answer — an author writing "show this
// only if" wants the content ABSENT for everyone else, and a recipient with nothing recorded is
// part of everyone else.
import { z } from 'zod';

/**
 * A test over one data point, held STRUCTURED — never as a Liquid expression string.
 *
 * The string form is the shape `fieldBindings` already uses and it would look like the
 * consistent choice. It is not, and it cannot be undone once analysts have authored deeds
 * against it: an opaque expression cannot be read back in plain English, cannot be
 * type-checked against the data point it names, cannot be re-pointed when a catalogue path is
 * renamed, and turns "which recipients match this?" into executing author-written code once per
 * investor. Compiling this down to a comparison is always available; parsing one back up is not.
 *
 * Deliberately only the tests that cannot be silently wrong. `isBlank`/`isYes` need no operand
 * type, `inCohort` defers the question to a governed grouping, and `isOneOf` is offered only for
 * data points that declare where their values come from (`DataPoint.choices`) — so the author
 * PICKS "United States" from the same list the value was chosen from rather than typing it, and
 * cannot write "Scotland" against a column holding "GB-SCT".
 *
 * Ordered comparisons (`>`/`<`) are still refused: they need a numeric or date type the
 * catalogue does not carry, and untyped they compare as text — `"9" > "10"` — which fails
 * invisibly in an executed deed.
 */
const predicateSchema = z.discriminatedUnion('operator', [
	z.object({ operator: z.literal('isBlank'), dataPointPath: z.string().min(1) }),
	z.object({ operator: z.literal('isNotBlank'), dataPointPath: z.string().min(1) }),
	z.object({ operator: z.literal('isYes'), dataPointPath: z.string().min(1) }),
	z.object({ operator: z.literal('isNo'), dataPointPath: z.string().min(1) }),
	// Equality against a CHOSEN value, never a typed one. A list rather than a single value so
	// "the United States or Canada" needs no `or`, which is the operator this design refuses.
	z.object({
		operator: z.literal('isOneOf'),
		dataPointPath: z.string().min(1),
		values: z.array(z.string().min(1)).min(1),
	}),
	z.object({
		operator: z.literal('isNoneOf'),
		dataPointPath: z.string().min(1),
		values: z.array(z.string().min(1)).min(1),
	}),
	// Membership is hand-maintained in Classifiers, so the id is the durable reference and the
	// name is only what the editor last saw it called.
	z.object({
		operator: z.literal('inCohort'),
		cohortId: z.string().min(1),
		cohortName: z.string().optional(),
	}),
]);
export type FlowPredicate = z.infer<typeof predicateSchema>;

const ruleSchema = z.object({
	/** Shown on the document beside the content it controls. */
	label: z.string(),
	when: predicateSchema,
});
export type FlowRule = z.infer<typeof ruleSchema>;

export const flowRulesSchema = z.record(ruleSchema);
export type FlowRules = z.infer<typeof flowRulesSchema>;

/**
 * What a test resolved to. Three-valued so the preview can say WHY content is hidden — "no" and
 * "nobody has answered" hide it alike, but they are different things to tell an author.
 */
export type FlowTernary = 'true' | 'false' | 'unknown';

/** Answers one test for one recipient. Injected so this module never imports a Liquid engine. */
export type FlowPredicateEvaluator = (predicate: FlowPredicate) => FlowTernary;

/** What one rule does to a recipient's copy. */
export type FlowRuleDecision = 'shown' | 'hidden';

/** A rule's answer for this recipient and what it does with it: shown only on a clear yes. */
export const decideFlowRule = (
	rule: FlowRule,
	evaluate: FlowPredicateEvaluator
): { answer: FlowTernary; decision: FlowRuleDecision } => {
	const answer = evaluate(rule.when);
	return { answer, decision: answer === 'true' ? 'shown' : 'hidden' };
};

/** The rules whose content this recipient keeps. */
export const shownFlowRules = (rules: FlowRules, evaluate: FlowPredicateEvaluator): Set<string> =>
	new Set(
		Object.entries(rules)
			.filter(([, rule]) => decideFlowRule(rule, evaluate).decision === 'shown')
			.map(([ruleId]) => ruleId)
	);
